// ── Platform Status page (coach-facing) ──────────────────────
// Fetches /coach-platform-status (returns hero status + per-service
// 90-day uptime strip + recent incidents) and renders into the
// #status-page-shell container. Refreshes live via the existing
// supabase-realtime channel on platform_health_snapshot — see the
// realtime watcher in app.js.
//
// The endpoint is auth-gated to any coach JWT (not just owner), so
// every logged-in coach can see this page. Admin-only metrics (DB
// pool %, AI cost, etc.) are filtered server-side.

let _supabase = null;
let _refreshing = false;
let _lastFetchAt = 0;

function setSupabase(s) { _supabase = s; }

function esc(s) {
  return String(s ?? '').replace(/[&<>"']/g, (c) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;',
  })[c]);
}

function relTime(iso) {
  if (!iso) return '—';
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 60_000)     return `${Math.max(1, Math.round(ms / 1000))}s ago`;
  if (ms < 3_600_000)  return `${Math.round(ms / 60_000)}m ago`;
  if (ms < 86_400_000) return `${Math.round(ms / 3_600_000)}h ago`;
  return `${Math.round(ms / 86_400_000)}d ago`;
}

function pillClassForStatus(status) {
  if (status === 'incident') return 'status-service-pill danger';
  if (status === 'warn')     return 'status-service-pill warn';
  if (status === 'no-data')  return 'status-service-pill muted';
  return 'status-service-pill';
}
function pillLabelForStatus(status) {
  if (status === 'incident') return '⛔ Outage';
  if (status === 'warn')     return '⚠ Degraded';
  if (status === 'no-data')  return '— No data';
  return '✓ Operational';
}

function heroClass(status) {
  if (status === 'incident') return 'status-hero danger';
  if (status === 'warn')     return 'status-hero warn';
  return 'status-hero';
}
function heroTitle(status, services) {
  if (status === 'incident') {
    const bad = services.filter((s) => s.currentStatus === 'incident').map((s) => s.name);
    return `Major outage — ${bad.join(', ')}`;
  }
  if (status === 'warn') {
    const bad = services.filter((s) => s.currentStatus === 'warn').map((s) => s.name);
    return `Degraded service — ${bad.join(', ')}`;
  }
  return 'All systems operational';
}
function heroGlyph(status) {
  if (status === 'incident') return '!';
  if (status === 'warn')     return '!';
  return '✓';
}

// Format date like "13 May 2026" — matches status.claude.com tooltip header.
function formatTooltipDate(ymd) {
  if (!ymd) return '';
  const [y, m, d] = ymd.split('-').map(Number);
  if (!y || !m || !d) return ymd;
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${d} ${months[m - 1]} ${y}`;
}

// Format downtime duration like "1 hr 5 min" / "55 min" / "2 hrs 12 min".
function formatDowntime(minutes) {
  if (!minutes || minutes < 1) return '';
  const hrs = Math.floor(minutes / 60);
  const min = minutes % 60;
  if (hrs === 0) return `${min} min`;
  if (min === 0) return `${hrs} hr${hrs > 1 ? 's' : ''}`;
  return `${hrs} hr${hrs > 1 ? 's' : ''} ${min} min`;
}

// Build the tooltip body for a single strip cell. Status-claude.com-style
// card with date, status label + duration, and related incidents list.
function buildCellTooltip(cell) {
  const date = formatTooltipDate(cell.day);
  if (cell.status === 'no-data') {
    return `
      <div class="status-tooltip-date">${esc(date)}</div>
      <div class="status-tooltip-empty">No data recorded for this day.</div>
    `;
  }
  if (cell.status === 'ok') {
    return `
      <div class="status-tooltip-date">${esc(date)}</div>
      <div class="status-tooltip-empty">No downtime recorded on this day.</div>
    `;
  }
  const label = cell.status === 'incident' ? 'Major outage'
              : cell.status === 'warn'     ? 'Partial outage'
              : cell.status === 'error'    ? 'Data unavailable'
              : cell.status;
  const glyph = cell.status === 'incident' ? '⛔' : '⚠';
  const cls = cell.status === 'incident' ? 'incident' : 'warn';
  const duration = formatDowntime(cell.downtimeMin);
  const related = (cell.incidents || []).slice(0, 4);

  return `
    <div class="status-tooltip-date">${esc(date)}</div>
    <div class="status-tooltip-status ${cls}">
      <span class="glyph">${glyph}</span>
      <span class="label">${esc(label)}</span>
      ${duration ? `<span class="duration">${esc(duration)}</span>` : ''}
    </div>
    ${related.length > 0 ? `
      <div class="status-tooltip-related-label">Related</div>
      <div class="status-tooltip-related-list">
        ${related.map((m) => `<div class="status-tooltip-related-item">${esc(m)}</div>`).join('')}
      </div>
    ` : ''}
  `;
}

function renderStrip(strip, rollupPct, svcIdx) {
  const cells = strip.map((c, idx) => {
    const cls = `status-strip-cell ${c.status}`;
    // svc+cell idx lets the global hover handler reconstitute the tooltip
    // body from the cached services array. Keeps DOM payload tiny.
    return `<div class="${cls}" data-svc-idx="${svcIdx}" data-cell-idx="${idx}"></div>`;
  }).join('');
  const rollupClass = rollupPct == null
    ? ''
    : rollupPct >= 99.5 ? '' : rollupPct >= 99 ? 'warn' : 'danger';
  const rollupText = rollupPct == null ? '—' : `${rollupPct.toFixed(2)}%`;
  return `
    <div class="status-strip" data-strip>${cells}</div>
    <div class="status-strip-foot">
      <span>90 days ago</span>
      <span class="status-strip-rollup ${rollupClass}">${rollupText} uptime</span>
      <span>Today</span>
    </div>
  `;
}

function renderService(svc, idx) {
  return `
    <div class="status-service-row">
      <div class="status-service-head">
        <div class="status-service-name">${esc(svc.name)}</div>
        <span class="${pillClassForStatus(svc.currentStatus)}">${pillLabelForStatus(svc.currentStatus)}</span>
      </div>
      ${renderStrip(svc.uptimeStrip || [], svc.rollupPct, idx)}
    </div>
  `;
}

function renderIncidents(incidents) {
  if (!incidents || incidents.length === 0) {
    return `
      <div class="status-incidents-section">
        <div class="status-incidents-title">Recent incidents — last 7 days</div>
        <div class="status-incidents-list">
          <div class="status-incidents-empty">No incidents in the last 7 days. ✓</div>
        </div>
      </div>
    `;
  }
  const rows = incidents.map((i) => {
    const cls = i.status === 'incident' ? 'incident' : 'warn';
    const meta = `${esc(i.source)} · started ${relTime(i.startedAt)}`;
    return `
      <div class="status-incident-row ${cls}">
        <div class="status-incident-glyph"></div>
        <div class="status-incident-body">
          <div class="status-incident-msg">${esc(i.message || `${i.source} ${i.status}`)}</div>
          <div class="status-incident-meta">${meta}</div>
        </div>
      </div>
    `;
  }).join('');
  return `
    <div class="status-incidents-section">
      <div class="status-incidents-title">Recent incidents — last 7 days</div>
      <div class="status-incidents-list">${rows}</div>
    </div>
  `;
}

async function loadStatusPage() {
  if (_refreshing) return;
  _refreshing = true;
  const shell = document.getElementById('status-page-shell');
  if (!shell) { _refreshing = false; return; }

  try {
    const { data: { session } } = await _supabase.auth.getSession();
    const token = session?.access_token;
    if (!token) {
      shell.innerHTML = '<div class="status-loading">Please sign in to view the status page.</div>';
      return;
    }
    const res = await fetch('/.netlify/functions/coach-platform-status', {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!res.ok) {
      const txt = await res.text().catch(() => '');
      console.error('[status] fetch failed', res.status, txt);
      shell.innerHTML = `<div class="status-loading">Couldn't load status (HTTP ${res.status}).</div>`;
      return;
    }
    const data = await res.json();
    _lastFetchAt = Date.now();
    renderStatusPage(shell, data);
  } catch (err) {
    console.error('[status] load error', err);
    shell.innerHTML = `<div class="status-loading">Network error loading status.</div>`;
  } finally {
    _refreshing = false;
  }
}

// Module-level cache so the global hover handler can rebuild a tooltip
// body without re-fetching. Updated on each renderStatusPage call.
let _currentServices = [];

function renderStatusPage(shell, data) {
  const { heroStatus = 'ok', services = [], incidents = [], polledAt } = data || {};
  _currentServices = services;
  shell.innerHTML = `
    <div class="${heroClass(heroStatus)}">
      <div class="status-hero-disc">${heroGlyph(heroStatus)}</div>
      <div class="status-hero-body">
        <div class="status-hero-title">${esc(heroTitle(heroStatus, services))}</div>
        <div class="status-hero-sub">${heroStatus === 'ok'
          ? `${services.length} services monitored · refreshed ${relTime(polledAt)}`
          : 'Investigating — see service details below'}</div>
      </div>
      <div class="status-hero-meta">Refreshed<br>${relTime(polledAt)}</div>
    </div>

    <div class="status-services">
      ${services.map((svc, idx) => renderService(svc, idx)).join('')}
    </div>

    ${renderIncidents(incidents)}
  `;
  ensureTooltipWired();
}

// ── Tooltip wiring ───────────────────────────────────────────
// Delegates from #status-page-shell so we don't need to attach per-cell
// listeners (90 cells × 7 services = 630 elements per render).
let _tooltipEl = null;
let _tooltipWired = false;

function ensureTooltipWired() {
  if (_tooltipWired) return;
  _tooltipWired = true;

  // Singleton tooltip element appended to body. Positioned absolutely
  // above the hovered cell, hidden by default.
  _tooltipEl = document.createElement('div');
  _tooltipEl.className = 'status-tooltip hidden';
  _tooltipEl.setAttribute('role', 'tooltip');
  document.body.appendChild(_tooltipEl);

  const shell = document.getElementById('status-page-shell');
  if (!shell) return;

  shell.addEventListener('mouseover', (e) => {
    const cell = e.target.closest('.status-strip-cell');
    if (!cell) return;
    const svcIdx = Number(cell.dataset.svcIdx);
    const cellIdx = Number(cell.dataset.cellIdx);
    if (!Number.isFinite(svcIdx) || !Number.isFinite(cellIdx)) return;
    const svc = _currentServices[svcIdx];
    const data = svc?.uptimeStrip?.[cellIdx];
    if (!data) return;
    showTooltip(cell, data);
  });
  shell.addEventListener('mouseout', (e) => {
    const cell = e.target.closest('.status-strip-cell');
    if (!cell) return;
    // Hide unless mouse moved onto the tooltip itself (rare with our offset)
    if (e.relatedTarget && _tooltipEl.contains(e.relatedTarget)) return;
    hideTooltip();
  });

  // Scroll closes the tooltip — cell position changes.
  window.addEventListener('scroll', hideTooltip, { passive: true, capture: true });
}

function showTooltip(cell, cellData) {
  if (!_tooltipEl) return;
  _tooltipEl.innerHTML = buildCellTooltip(cellData);
  _tooltipEl.classList.remove('hidden');
  // Position: centered horizontally on the cell, below it by 8px gap.
  const rect = cell.getBoundingClientRect();
  const ttRect = _tooltipEl.getBoundingClientRect();
  const cellCenterX = rect.left + rect.width / 2;
  let left = cellCenterX - ttRect.width / 2;
  // Clamp to viewport
  const minMargin = 8;
  if (left < minMargin) left = minMargin;
  if (left + ttRect.width > window.innerWidth - minMargin) {
    left = window.innerWidth - ttRect.width - minMargin;
  }
  let top = rect.bottom + 8;
  // If tooltip would overflow bottom of viewport, flip above the cell.
  if (top + ttRect.height > window.innerHeight - minMargin) {
    top = rect.top - ttRect.height - 8;
  }
  _tooltipEl.style.left = `${Math.round(left + window.scrollX)}px`;
  _tooltipEl.style.top = `${Math.round(top + window.scrollY)}px`;
}

function hideTooltip() {
  if (_tooltipEl) _tooltipEl.classList.add('hidden');
}

// ── Realtime hook ────────────────────────────────────────────
// Subscribes when the Status view becomes active. The Realtime channel
// fires on every platform_health_snapshot INSERT; debounce 500ms so a
// batch poller doesn't trigger N renders. Unsubscribes when leaving.
let _rtChannel = null;
let _rtDebounce = null;

function startRealtime() {
  if (_rtChannel || !_supabase) return;
  try {
    _rtChannel = _supabase.channel('coach-status-rt')
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'platform_health_snapshot' }, () => {
        if (_rtDebounce) clearTimeout(_rtDebounce);
        _rtDebounce = setTimeout(() => loadStatusPage(), 500);
      })
      .subscribe();
  } catch (e) {
    console.warn('[status-rt] start failed:', e.message);
  }
}

function stopRealtime() {
  if (_rtChannel && _supabase) {
    try { _supabase.removeChannel(_rtChannel); } catch {}
    _rtChannel = null;
  }
  if (_rtDebounce) { clearTimeout(_rtDebounce); _rtDebounce = null; }
}

function initStatusPage({ supabase }) {
  setSupabase(supabase);

  // Refresh + start realtime whenever the Status view is activated.
  document.querySelector('[data-view="status"]')?.addEventListener('click', () => {
    loadStatusPage();
    startRealtime();
  });

  // If page loads with Status as the restored view, fire load immediately.
  const savedView = (() => { try { return localStorage.getItem('revfit_last_view'); } catch { return null; } })();
  if (savedView === 'status') {
    loadStatusPage();
    startRealtime();
  }

  // Stop the realtime channel when navigating away (any other view click).
  document.querySelectorAll('.nav-btn').forEach((btn) => {
    if (btn.dataset.view === 'status') return;
    btn.addEventListener('click', () => stopRealtime());
  });
}

export { initStatusPage, loadStatusPage };
